import React, { useState, useEffect } from 'react';
import { Organization, User } from '../../types';

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (userData: {
    name: string;
    email: string;
    jobTitle?: string;
    organizationId: number;
    password?: string;
    autoGenerate: boolean;
    sendWelcomeEmail: boolean;
  }) => void;
  organization: Organization;
  user?: User | null; // For edit mode
  isLoading?: boolean;
}

const UserModal: React.FC<UserModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  organization,
  user = null,
  isLoading = false
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [autoGeneratePassword, setAutoGeneratePassword] = useState(true);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [manualPassword, setManualPassword] = useState('');
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
  const [errors, setErrors] = useState<{ 
    name?: string; 
    email?: string; 
    password?: string; 
  }>({});
  const [isVisible, setIsVisible] = useState(false);

  // Generate cryptographically secure random password
  const generatePassword = () => {
    const currentYear = new Date().getFullYear().toString();
    const prefix = `QFM${currentYear}-`;
    
    // Character sets to ensure we have uppercase, lowercase, and numbers
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const allChars = uppercase + lowercase + numbers;
    
    // Calculate remaining length needed (minimum 12 total - prefix length)
    const minTotalLength = 12;
    const remainingLength = Math.max(minTotalLength - prefix.length, 6); // At least 6 chars after prefix
    
    // Use crypto.getRandomValues for cryptographically secure random generation
    const getSecureRandom = (max: number): number => {
      const array = new Uint32Array(1);
      crypto.getRandomValues(array);
      return array[0] % max;
    };
    
    let randomPart = '';
    
    // Ensure at least one character from each required set
    randomPart += uppercase.charAt(getSecureRandom(uppercase.length));
    randomPart += lowercase.charAt(getSecureRandom(lowercase.length));
    randomPart += numbers.charAt(getSecureRandom(numbers.length));
    
    // Fill the remaining length with random characters from all sets
    for (let i = 3; i < remainingLength; i++) {
      randomPart += allChars.charAt(getSecureRandom(allChars.length));
    }
    
    // Shuffle the random part to avoid predictable patterns
    const shuffled = randomPart.split('').sort(() => {
      const array = new Uint32Array(1);
      crypto.getRandomValues(array);
      return (array[0] % 3) - 1; // -1, 0, or 1 for shuffling
    }).join('');
    
    return prefix + shuffled;
  };

  // Initialize form when modal opens
  useEffect(() => {
    if (isOpen) {
      if (user) {
        // Edit mode - populate with existing user data
        setName(user.name || '');
        setEmail(user.email || '');
        setJobTitle(user.job_title || '');
        setAutoGeneratePassword(false);
        setGeneratedPassword('');
        setManualPassword('');
        setSendWelcomeEmail(false);
      } else {
        // Add mode - reset to defaults
        setName('');
        setEmail('');
        setJobTitle('');
        setAutoGeneratePassword(true);
        setGeneratedPassword(generatePassword());
        setManualPassword('');
        setSendWelcomeEmail(true);
      }
      setErrors({});
      
      // Trigger fade in animation
      setTimeout(() => setIsVisible(true), 10);
    } else {
      setIsVisible(false);
    }
  }, [isOpen, user]);

  // Regenerate password when auto-generate is toggled on
  useEffect(() => {
    if (autoGeneratePassword) {
      setGeneratedPassword(generatePassword());
    }
  }, [autoGeneratePassword]);

  const validateForm = () => {
    const newErrors: { name?: string; email?: string; password?: string } = {};
    
    if (!name.trim()) {
      newErrors.name = 'Full name is required';
    } else if (name.trim().length < 2) {
      newErrors.name = 'Full name must be at least 2 characters';
    } else if (name.trim().length > 100) {
      newErrors.name = 'Full name must not exceed 100 characters';
    }
    
    if (!email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation - required for new users, optional for edit
    if (!user && !autoGeneratePassword) {
      if (!manualPassword) {
        newErrors.password = 'Password is required when not auto-generated';
      } else if (manualPassword.length < 6) {
        newErrors.password = 'Password must be at least 6 characters';
      }
    } else if (user && manualPassword && manualPassword.length < 6) {
      newErrors.password = 'Password must be at least 6 characters if provided';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Real-time validation for individual fields
  const validateField = (fieldName: string, value: string) => {
    const newErrors = { ...errors };
    
    switch (fieldName) {
      case 'name':
        if (!value.trim()) {
          newErrors.name = 'Full name is required';
        } else if (value.trim().length < 2) {
          newErrors.name = 'Full name must be at least 2 characters';
        } else if (value.trim().length > 100) {
          newErrors.name = 'Full name must not exceed 100 characters';
        } else {
          delete newErrors.name;
        }
        break;
      
      case 'email':
        if (!value.trim()) {
          newErrors.email = 'Email address is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
          newErrors.email = 'Please enter a valid email address';
        } else {
          delete newErrors.email;
        }
        break;
      
      case 'password':
        if (!user && !autoGeneratePassword) {
          if (!value) {
            newErrors.password = 'Password is required when not auto-generated';
          } else if (value.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
          } else {
            delete newErrors.password;
          }
        } else if (user && value && value.length < 6) {
          newErrors.password = 'Password must be at least 6 characters if provided';
        } else {
          delete newErrors.password;
        }
        break;
    }
    
    setErrors(newErrors);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onConfirm({
        name: name.trim(),
        email: email.trim(),
        jobTitle: jobTitle.trim() || undefined,
        organizationId: organization.id,
        password: autoGeneratePassword ? generatedPassword : manualPassword,
        autoGenerate: autoGeneratePassword,
        sendWelcomeEmail
      });
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setIsVisible(false);
      // Delay the actual close to allow fade out animation
      setTimeout(() => {
        onClose();
      }, 200);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedPassword);
    } catch (err) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = generatedPassword;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity duration-200 z-50 ${
      isVisible ? 'opacity-100' : 'opacity-0'
    }`}>
      <div className="fixed inset-0 z-10 overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <div className={`relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all duration-200 sm:my-8 sm:w-full sm:max-w-lg ${
            isVisible ? 'opacity-100 translate-y-0 sm:scale-100' : 'opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
          }`}>
            <form onSubmit={handleSubmit}>
              <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                    {/* Header with border */}
                    <h3 className="text-xl font-bold leading-6 text-gray-900 mb-4 pb-4 border-b border-gray-200">
                      {user ? 'Edit User' : 'Add User'}
                    </h3>

                    <div className="space-y-4">
                      {/* Full Name Field */}
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                          Full Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          id="name"
                          value={name}
                          maxLength={100}
                          onChange={(e) => {
                            setName(e.target.value);
                            // Only validate if there's already an error to avoid showing errors while typing
                            if (errors.name) {
                              validateField('name', e.target.value);
                            }
                          }}
                          className={`block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6 ${
                            errors.name ? 'ring-red-300 focus:ring-red-500' : ''
                          }`}
                          placeholder="Enter full name"
                          disabled={isLoading}
                        />
                        {errors.name && (
                          <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                        )}
                      </div>

                      {/* Email Address Field */}
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            // Only validate if there's already an error to avoid showing errors while typing
                            if (errors.email) {
                              validateField('email', e.target.value);
                            }
                          }}
                          className={`block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6 ${
                            errors.email ? 'ring-red-300 focus:ring-red-500' : ''
                          }`}
                          placeholder="Enter email address"
                          disabled={isLoading}
                        />
                        {errors.email && (
                          <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                        )}
                      </div>

                      {/* Job Title Field */}
                      <div>
                        <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-700 mb-1">
                          Job Title (optional)
                        </label>
                        <input
                          type="text"
                          id="jobTitle"
                          value={jobTitle}
                          onChange={(e) => setJobTitle(e.target.value)}
                          className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                          placeholder="Enter job title"
                          disabled={isLoading}
                        />
                      </div>

                      {/* Organization Field (readonly) */}
                      <div>
                        <label htmlFor="organization" className="block text-sm font-medium text-gray-700 mb-1">
                          Organization
                        </label>
                        <input
                          type="text"
                          id="organization"
                          value={organization.name}
                          readOnly
                          className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 bg-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 sm:text-sm sm:leading-6 cursor-not-allowed"
                        />
                      </div>

                      {/* Auto-generate Password Checkbox - Only in Add mode */}
                      {!user && (
                        <div className="flex items-center">
                          <input
                            id="autoGenerate"
                            type="checkbox"
                            checked={autoGeneratePassword}
                            onChange={(e) => setAutoGeneratePassword(e.target.checked)}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            disabled={isLoading}
                          />
                          <label htmlFor="autoGenerate" className="ml-2 text-sm text-gray-700">
                            Auto-generate Password
                          </label>
                        </div>
                      )}

                      {/* Generated Password Field - Only in Add mode */}
                      {!user && autoGeneratePassword && (
                        <div>
                          <label htmlFor="generatedPassword" className="block text-sm font-medium text-gray-700 mb-1">
                            Temporary Password
                          </label>
                          <div className="flex">
                            <input
                              type="text"
                              id="generatedPassword"
                              value={generatedPassword}
                              readOnly
                              className="block w-full rounded-l-md border-2 border-dashed border-gray-300 py-1.5 px-3 text-gray-900 bg-gray-50 sm:text-sm sm:leading-6 cursor-not-allowed"
                            />
                            <button
                              type="button"
                              onClick={copyToClipboard}
                              className="inline-flex items-center px-3 py-1.5 border-2 border-l-0 border-dashed border-gray-300 rounded-r-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                              disabled={isLoading}
                            >
                              📋 Copy
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Manual Password Field */}
                      {(user || !autoGeneratePassword) && (
                        <div>
                          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                            {user ? 'New Password (optional)' : 'Password'} {!user && <span className="text-red-500">*</span>}
                          </label>
                          <input
                            type="password"
                            id="password"
                            value={manualPassword}
                            onChange={(e) => {
                              setManualPassword(e.target.value);
                              // Only validate if there's already an error to avoid showing errors while typing
                              if (errors.password) {
                                validateField('password', e.target.value);
                              }
                            }}
                            className={`block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6 ${
                              errors.password ? 'ring-red-300 focus:ring-red-500' : ''
                            }`}
                            placeholder={user ? "Leave blank to keep current password" : "Enter password (min 6 characters)"}
                            disabled={isLoading}
                          />
                          {errors.password && (
                            <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                          )}
                        </div>
                      )}

                      {/* Send Welcome Email Checkbox - Only in Add mode */}
                      {!user && (
                        <div className="flex items-center">
                          <input
                            id="sendWelcomeEmail"
                            type="checkbox"
                            checked={sendWelcomeEmail}
                            onChange={(e) => setSendWelcomeEmail(e.target.checked)}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                            disabled={isLoading}
                          />
                          <label htmlFor="sendWelcomeEmail" className="ml-2 text-sm text-gray-700">
                            Send Welcome Email
                          </label>
                        </div>
                      )}
                      {/* Manual Communication Notice - Only in Add mode */}
                      {!user && (
                        <div className="bg-blue-50 border-l-4 border-l-radius border-blue-400 p-4 rounded-r-md border-radius">
                          <div className="flex items-center">
                              <div className="flex-shrink-0">
                                  <span className="text-blue-400 text-xl">📧</span>
                              </div>
                              <div className="ml-3">
                                  <h4 className="text-base font-medium">
                                      Manual Communication Required
                                  </h4>
                              </div>
                          </div>
                          <p className="mt-1 text-sm">
                              After creating the user, you'll need to manually communicate the login credentials. Welcome email functionality will be added in a future update.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="inline-flex w-full justify-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {user ? 'Updating User...' : 'Adding User...'}
                    </>
                  ) : (
                    user ? 'Update User' : 'Add User'
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isLoading}
                  className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserModal;